"use strict";
// Classic deferred asset, loaded before existing theme/Speedy submit guards.
// It saves only owned cart attributes and then replays the original submit.
(() => {
    const selector = 'erp-company-invoice';
    if (customElements.get(selector))
        return;
    const fields = [
        'version',
        'requested',
        'company',
        'registration',
        'vat',
        'same_as_shipping',
        'country',
        'postal_code',
        'city',
        'address1',
        'address2',
    ];
    const components = new Set();
    let values;
    let revision = 0;
    let savedRevision = 0;
    let timer;
    let queue = Promise.resolve();
    let submitting = false;
    const attributeName = (field) => `attributes[erp_invoice_${field}]`;
    function input(root, name) {
        const element = root.querySelector(name);
        if (!element)
            throw new Error('INVOICE_FORM_INCOMPLETE');
        return element;
    }
    class CompanyInvoice extends HTMLElement {
        mounted = false;
        connectedCallback() {
            if (this.mounted)
                return;
            this.mounted = true;
            components.add(this);
            values ??= this.read();
            this.apply(values);
            input(this, '[data-invoice-toggle]').disabled = false;
            this.addEventListener('input', this.changed);
            this.addEventListener('change', this.changed);
        }
        disconnectedCallback() {
            components.delete(this);
            this.removeEventListener('input', this.changed);
            this.removeEventListener('change', this.changed);
            this.mounted = false;
        }
        read() {
            return Object.fromEntries(fields.map((field) => [
                field,
                input(this, `[name="${attributeName(field)}"]`)
                    .value,
            ]));
        }
        apply(next) {
            for (const field of fields) {
                const control = input(this, `[name="${attributeName(field)}"]`);
                if (control.value !== next[field])
                    control.value = next[field];
            }
            const requested = next.requested === 'true';
            const same = next.same_as_shipping !== 'false';
            input(this, '[data-invoice-toggle]').checked = requested;
            input(this, '[data-invoice-same]').checked = same;
            input(this, '[data-invoice-fields]').hidden = !requested;
            input(this, '[data-invoice-address]').hidden = same;
            for (const control of this.querySelectorAll('[data-invoice-field]')) {
                const enabled = requested && (!control.closest('[data-invoice-address]') || !same);
                control.disabled = !enabled;
                control.required = enabled && control.dataset.optional !== 'true';
            }
            const postcode = input(this, `[name="${attributeName('postal_code')}"]`);
            if (next.country === 'BG')
                postcode.pattern = '[0-9]{4}';
            else
                postcode.removeAttribute('pattern');
            this.closest('form')?.classList.toggle('erp-company-invoice-active', requested);
        }
        changed = (event) => {
            if (!(event.target instanceof HTMLElement) || !event.target.closest(selector))
                return;
            const next = this.read();
            next.version = '1';
            next.requested = input(this, '[data-invoice-toggle]').checked
                ? 'true'
                : 'false';
            next.same_as_shipping = input(this, '[data-invoice-same]').checked
                ? 'true'
                : 'false';
            if (JSON.stringify(next) === JSON.stringify(values))
                return;
            values = next;
            revision += 1;
            for (const component of components) {
                component.apply(next);
                component.status('');
            }
            if (timer)
                clearTimeout(timer);
            timer = setTimeout(() => {
                void save().catch(() => showSaveError());
            }, 600);
        };
        status(message, error = false) {
            const status = input(this, '[data-invoice-status]');
            status.textContent = message;
            status.classList.toggle('erp-invoice__error', error);
        }
        valid() {
            if (values?.requested !== 'true')
                return true;
            const invalid = [
                ...this.querySelectorAll('[data-invoice-field]'),
            ].find((control) => !control.disabled &&
                (!control.checkValidity() || (control.required && !control.value.trim())));
            if (!invalid)
                return true;
            this.status(this.dataset.invalid ?? 'Please complete the required company invoice details.', true);
            invalid.focus();
            invalid.reportValidity();
            return false;
        }
    }
    function showSaveError() {
        for (const component of components)
            component.status(component.dataset.saveError ?? 'Could not save invoice details. Please try again.', true);
    }
    async function save() {
        if (timer)
            clearTimeout(timer);
        timer = undefined;
        if (!values || savedRevision === revision)
            return queue;
        const currentRevision = revision;
        const attributes = Object.fromEntries(fields.map((field) => [`erp_invoice_${field}`, values[field].trim()]));
        const component = components.values().next().value;
        const url = component?.dataset.updateUrl;
        if (!url)
            throw new Error('INVOICE_SAVE_UNAVAILABLE');
        queue = queue
            .catch(() => undefined)
            .then(async () => {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ attributes }),
                signal: AbortSignal.timeout(15000),
            });
            if (!response.ok)
                throw new Error('INVOICE_SAVE_FAILED');
            const body = await response.json();
            if (!body ||
                typeof body !== 'object' ||
                !('attributes' in body) ||
                !body.attributes ||
                typeof body.attributes !== 'object')
                throw new Error('INVOICE_SAVE_FAILED');
            const saved = body.attributes;
            if (Object.entries(attributes).some(([key, value]) => value && saved[key] !== value))
                throw new Error('INVOICE_SAVE_FAILED');
            savedRevision = currentRevision;
        });
        return queue;
    }
    document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement))
            return;
        const component = form.querySelector(selector);
        const button = event.submitter;
        if (!component || !(button instanceof HTMLButtonElement) || button.name !== 'checkout')
            return;
        if (!component.valid()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }
        if (savedRevision === revision)
            return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (submitting)
            return;
        submitting = true;
        component.status(component.dataset.saving ?? 'Saving invoice details…');
        void save()
            .then(() => {
            component.status('');
            submitting = false;
            if (form.isConnected && button.isConnected && !button.disabled)
                form.requestSubmit(button);
        })
            .catch(() => {
            submitting = false;
            showSaveError();
        });
    }, true);
    customElements.define(selector, CompanyInvoice);
})();
